import { ProductDocument, CategorieDocument } from "@/prismicio-types";
import { EnrichedProduct } from "@/types";
import { getPermalink } from "@/lib/i18n/functions";
import { StoreProduct } from "@medusajs/types";
import ProductCard from "@/components/molecules/product-card";
import { Locales } from "@/lib/i18n";
import { isSaleProduct } from "@/lib/utils";
import { resolveProductConditioning } from "@/lib/product-units";
import { isProductOrderable } from "@/lib/product-availability";

const isEnrichedProduct = (product: ProductDocument | EnrichedProduct): product is EnrichedProduct => {
  return typeof product === "object" && product !== null && "prismic" in product && "category" in product;
};

type RenderProductCardProps = {
  product: ProductDocument | EnrichedProduct;
  medusaProduct: StoreProduct | undefined;
  category: CategorieDocument | undefined;
  locale: Locales;
  isLoading: boolean;
  className?: string;
  onQuantityChange?: (quantity: number) => void;
};

export const resolveProductCardProps = (
  product: ProductDocument,
  medusaProduct: StoreProduct | undefined,
  category: CategorieDocument | undefined,
  locale: Locales,
  onQuantityChange?: (quantity: number) => void
) => {
  return {
    productImage:
      medusaProduct?.images?.at(0)?.url ||
      product?.data?.images?.[0]?.image?.url ||
      product?.data?.featuredImage?.url ||
      "",
    productName: product.data.title || "",
    productPrice: medusaProduct?.variants?.[0]?.calculated_price?.calculated_amount || 0,
    productTag: category?.data?.title || "",
    reference: medusaProduct?.variants?.[0]?.sku || "ACBROMO",
    link: `${getPermalink(locale, "product")}/${product.uid}`,
    productId: product.uid || "",
    medusaId: medusaProduct?.id || "",
    medusaVariantId: medusaProduct?.variants?.[0]?.id || "",
    productConditioning: resolveProductConditioning(
      medusaProduct?.metadata?.conditioning,
      medusaProduct?.metadata?.conditioning_bac
    ),
    isCollection: product.data.productCollection || false,
    onQuantityChange,
    isRange: medusaProduct?.metadata?.is_surface_based as boolean,
    isPriceDynamic: medusaProduct?.metadata?.is_price_dynamic as boolean,
    forSale: isSaleProduct(medusaProduct),
  };
};

export const renderProductCard = ({
  product,
  medusaProduct,
  category,
  locale,
  isLoading,
  onQuantityChange,
  className,
}: RenderProductCardProps) => {
  if (isEnrichedProduct(product)) {
    return (
      <ProductCard
        locale={locale}
        productImage={
          medusaProduct?.images?.[0]?.url ||
          product.prismic.images?.[0]?.image.url ||
          product.prismic.featuredImage.url ||
          ""
        }
        medusaId={medusaProduct?.id || ""}
        medusaVariantId={medusaProduct?.variants?.[0]?.id || ""}
        productName={product.prismic.title ?? ""}
        productPrice={medusaProduct?.variants?.[0]?.calculated_price?.calculated_amount || 0}
        productTag={product.category.title}
        reference={medusaProduct?.variants?.[0]?.sku || "ACBROMO"}
        link={`${getPermalink(locale, "product")}/${product.prismic.uid}`}
        variant={product.prismic.image_full_size_card ? "image-only" : "default"}
        productId={product.prismic.uid}
        className={className}
        isCollection={product.prismic.isCollection}
        isPending={isLoading}
        productConditioning={resolveProductConditioning(
          medusaProduct?.metadata?.conditioning,
          medusaProduct?.metadata?.conditioning_bac
        )}
        onQuantityChange={onQuantityChange}
        isOutOfStock={!isProductOrderable(medusaProduct)}
        isRange={medusaProduct?.metadata?.is_surface_based as boolean}
        isPriceDynamic={medusaProduct?.metadata?.is_price_dynamic as boolean}
        forSale={isSaleProduct(medusaProduct)}
      />
    );
  }

  const resolved = resolveProductCardProps(product, medusaProduct, category, locale, onQuantityChange);

  return (
    <ProductCard
      locale={locale}
      {...resolved}
      variant={product.data.image_full_size_card ? "image-only" : "default"}
      className={className}
      isPending={isLoading}
      isOutOfStock={!isProductOrderable(medusaProduct)}
    />
  );
};
